// Upload Queue System with Rate Limiting
// Manages concurrent uploads with max concurrency and retry logic

import { AudioFile } from '@types';
import { UploadProgress } from '@types';

export interface UploadTask {
  id: string;
  file: File;
  metadata: Partial<AudioFile>;
  onProgress?: (progress: UploadProgress) => void;
  onComplete?: (file: AudioFile) => void;
  onError?: (error: Error) => void;
  retries: number;
  maxRetries: number;
  paused?: boolean;
  pausedAt?: number; // Timestamp when paused
}

export interface UploadQueueOptions {
  maxConcurrent?: number;
  maxRetries?: number;
  retryDelay?: number;
}

class UploadQueue {
  private queue: UploadTask[] = [];
  private active: UploadTask[] = [];
  private paused: UploadTask[] = []; // Paused uploads
  private maxConcurrent: number;
  private maxRetries: number;
  private retryDelay: number;
  private uploadFn?: (file: File, metadata: Partial<AudioFile>, onProgress?: (progress: UploadProgress) => void) => Promise<AudioFile>;
  private activeUploads: Map<string, { abortController?: AbortController }> = new Map();

  constructor(options: UploadQueueOptions = {}) {
    this.maxConcurrent = options.maxConcurrent || 5; // Increased to 5
    this.maxRetries = options.maxRetries || 3;
    this.retryDelay = options.retryDelay || 2000;
  }

  // Set the upload function
  setUploadFunction(fn: (file: File, metadata: Partial<AudioFile>, onProgress?: (progress: UploadProgress) => void) => Promise<AudioFile>) {
    this.uploadFn = fn;
  }

  // Add upload task to queue
  async add(task: Omit<UploadTask, 'id' | 'retries' | 'paused' | 'pausedAt'> & { maxRetries?: number }): Promise<AudioFile> {
    const uploadTask: UploadTask = {
      file: task.file,
      metadata: task.metadata,
      onProgress: task.onProgress,
      onComplete: task.onComplete,
      onError: task.onError,
      id: crypto.randomUUID(),
      retries: 0,
      maxRetries: task.maxRetries || this.maxRetries,
      paused: false,
    };

    return new Promise((resolve, reject) => {
      uploadTask.onComplete = (file) => {
        this.activeUploads.delete(uploadTask.id);
        resolve(file);
      };
      uploadTask.onError = (error) => {
        this.activeUploads.delete(uploadTask.id);
        reject(error);
      };
      
      this.queue.push(uploadTask);
      this.processQueue();
    });
  }

  // Process queue
  private async processQueue() {
    // Don't process if we're at max concurrency or no upload function
    if (this.active.length >= this.maxConcurrent || !this.uploadFn || this.queue.length === 0) {
      return;
    }

    // Get next task from queue
    const task = this.queue.shift();
    if (!task) return;

    // Skip if paused
    if (task.paused) {
      this.paused.push(task);
      this.processQueue(); // Process next
      return;
    }

    // Add to active
    this.active.push(task);

    // Create abort controller for this upload
    const abortController = new AbortController();
    this.activeUploads.set(task.id, { abortController });

    // Start upload
    this.executeUpload(task, abortController.signal)
      .then((file) => {
        // Remove from active
        this.active = this.active.filter(t => t.id !== task.id);
        this.activeUploads.delete(task.id);
        
        // Call onComplete
        task.onComplete?.(file);
        
        // Process next in queue
        this.processQueue();
      })
      .catch((error) => {
        // Remove from active
        this.active = this.active.filter(t => t.id !== task.id);
        this.activeUploads.delete(task.id);

        // Check if it was aborted (paused)
        if (error.name === 'AbortError' || abortController.signal.aborted) {
          // Upload was paused, don't retry
          task.paused = true;
          task.pausedAt = Date.now();
          this.paused.push(task);
          this.processQueue();
          return;
        }

        // Check error type for better error messages
        const isNetworkError = this.isNetworkError(error);
        const isStorageLimitError = this.isStorageLimitError(error);

        // Retry logic (only for retryable errors)
        if (task.retries < task.maxRetries && this.isRetryableError(error)) {
          task.retries++;
          const retryDelay = this.calculateRetryDelay(task.retries, isNetworkError);
          console.log(`🔄 Retrying upload ${task.file.name} (attempt ${task.retries}/${task.maxRetries}) - ${isNetworkError ? 'Network error' : 'Server error'}`);
          
          // Update progress with retry info
          task.onProgress?.({
            fileId: task.id,
            fileName: task.file.name,
            progress: 0,
            status: 'retrying',
            error: `Retrying... (${task.retries}/${task.maxRetries})`,
          });
          
          // Wait before retry with exponential backoff
          setTimeout(() => {
            if (!task.paused) {
              this.queue.unshift(task); // Add back to front of queue
              this.processQueue();
            }
          }, retryDelay);
        } else {
          // Max retries reached or non-retryable error
          const errorMessage = this.getErrorMessage(error, isNetworkError, isStorageLimitError);
          console.error(`❌ Upload failed after ${task.retries} attempts:`, task.file.name, errorMessage);
          
          // Update progress with final error
          task.onProgress?.({
            fileId: task.id,
            fileName: task.file.name,
            progress: 0,
            status: 'error',
            error: errorMessage,
          });
          
          task.onError?.(new Error(errorMessage));
          // Process next in queue
          this.processQueue();
        }
      });
  }

  // Execute upload
  private async executeUpload(task: UploadTask, signal?: AbortSignal): Promise<AudioFile> {
    if (!this.uploadFn) {
      throw new Error('Upload function not set');
    }

    // Check if aborted before starting
    if (signal?.aborted) {
      throw new DOMException('Upload was aborted', 'AbortError');
    }

    return this.uploadFn(task.file, task.metadata, (progress) => {
      // Check if aborted during progress updates
      if (signal?.aborted) {
        throw new DOMException('Upload was aborted', 'AbortError');
      }
      task.onProgress?.(progress);
    });
  }

  // Check if error is a network error
  private isNetworkError(error: any): boolean {
    if (!error) return false;
    
    const errorMessage = error.message?.toLowerCase() || '';
    const errorName = error.name?.toLowerCase() || '';
    
    return (
      errorName === 'networkerror' ||
      errorName === 'typeerror' ||
      errorMessage.includes('network') ||
      errorMessage.includes('fetch') ||
      errorMessage.includes('connection') ||
      errorMessage.includes('timeout') ||
      errorMessage.includes('failed to fetch') ||
      errorMessage.includes('network request failed')
    );
  }

  // Check if error is a storage limit error
  private isStorageLimitError(error: any): boolean {
    if (!error) return false;
    
    const errorMessage = error.message?.toLowerCase() || '';
    
    return (
      errorMessage.includes('storage limit') ||
      errorMessage.includes('storage_limit') ||
      errorMessage.includes('storage or bandwidth limit') ||
      errorMessage.includes('quota') ||
      errorMessage.includes('limit exceeded')
    );
  }

  // Check if error is retryable
  private isRetryableError(error: any): boolean {
    // Don't retry storage limit errors (user needs to upgrade)
    if (this.isStorageLimitError(error)) {
      return false;
    }
    
    // Retry network errors and server errors (5xx)
    return this.isNetworkError(error) || (error.status >= 500 && error.status < 600);
  }

  // Calculate retry delay with exponential backoff
  private calculateRetryDelay(retryCount: number, isNetworkError: boolean): number {
    // Network errors: longer delay (exponential backoff)
    // Server errors: shorter delay
    const baseDelay = isNetworkError ? this.retryDelay * 2 : this.retryDelay;
    return baseDelay * Math.pow(2, retryCount - 1);
  }

  // Get user-friendly error message
  private getErrorMessage(error: any, isNetworkError: boolean, isStorageLimitError: boolean): string {
    if (isStorageLimitError) {
      return 'Storage limit reached. Please upgrade your plan or free up space.';
    }
    
    if (isNetworkError) {
      return 'Network error. Please check your internet connection and try again.';
    }
    
    if (error.status === 413) {
      return 'File is too large. Maximum file size is 500MB.';
    }
    
    if (error.status === 401 || error.status === 403) {
      return 'Authentication failed. Please log in again.';
    }
    
    // Return original error message if available, otherwise generic message
    return error.message || 'Upload failed. Please try again.';
  }

  // Get queue status
  getStatus() {
    return {
      queueLength: this.queue.length,
      activeCount: this.active.length,
      pausedCount: this.paused.length,
      maxConcurrent: this.maxConcurrent,
      total: this.queue.length + this.active.length + this.paused.length,
    };
  }

  // Pause upload (abort current upload and move to paused)
  pause(taskId: string): boolean {
    // Check if in active uploads
    const activeTask = this.active.find(t => t.id === taskId);
    if (activeTask) {
      // Abort the upload
      const uploadInfo = this.activeUploads.get(taskId);
      if (uploadInfo?.abortController) {
        uploadInfo.abortController.abort();
      }
      
      // Mark as paused
      activeTask.paused = true;
      activeTask.pausedAt = Date.now();
      
      // Remove from active and add to paused
      this.active = this.active.filter(t => t.id !== taskId);
      this.activeUploads.delete(taskId);
      this.paused.push(activeTask);
      
      // Update progress
      activeTask.onProgress?.({
        fileId: activeTask.id,
        fileName: activeTask.file.name,
        progress: 0,
        status: 'paused',
      });
      
      // Process next in queue
      this.processQueue();
      return true;
    }
    
    // Check if in queue
    const queuedTask = this.queue.find(t => t.id === taskId);
    if (queuedTask) {
      queuedTask.paused = true;
      queuedTask.pausedAt = Date.now();
      this.queue = this.queue.filter(t => t.id !== taskId);
      this.paused.push(queuedTask);
      return true;
    }
    
    return false;
  }

  // Resume paused upload
  resume(taskId: string): boolean {
    const pausedTask = this.paused.find(t => t.id === taskId);
    if (pausedTask) {
      pausedTask.paused = false;
      pausedTask.pausedAt = undefined;
      this.paused = this.paused.filter(t => t.id !== taskId);
      
      // Add back to queue
      this.queue.unshift(pausedTask);
      
      // Update progress
      pausedTask.onProgress?.({
        fileId: pausedTask.id,
        fileName: pausedTask.file.name,
        progress: 0,
        status: 'uploading',
      });
      
      // Process queue
      this.processQueue();
      return true;
    }
    
    return false;
  }

  // Cancel upload (remove completely)
  cancel(taskId: string): boolean {
    // Abort if active
    const uploadInfo = this.activeUploads.get(taskId);
    if (uploadInfo?.abortController) {
      uploadInfo.abortController.abort();
    }
    
    // Remove from all lists
    this.queue = this.queue.filter(t => t.id !== taskId);
    this.active = this.active.filter(t => t.id !== taskId);
    this.paused = this.paused.filter(t => t.id !== taskId);
    this.activeUploads.delete(taskId);
    
    // Process next in queue
    this.processQueue();
    return true;
  }

  // Cancel upload by fileName (finds task by file name and cancels it)
  cancelByFileName(fileName: string): boolean {
    // Find task by fileName in all lists
    const findTask = (tasks: UploadTask[]) => tasks.find(t => t.file.name === fileName);
    
    const queuedTask = findTask(this.queue);
    const activeTask = findTask(this.active);
    const pausedTask = findTask(this.paused);
    
    const taskToCancel = queuedTask || activeTask || pausedTask;
    
    if (taskToCancel) {
      return this.cancel(taskToCancel.id);
    }
    
    return false;
  }

  // Cancel all uploads for given file names
  cancelByFileNames(fileNames: string[]): number {
    let cancelledCount = 0;
    fileNames.forEach(fileName => {
      if (this.cancelByFileName(fileName)) {
        cancelledCount++;
      }
    });
    return cancelledCount;
  }

  // Clear all uploads
  clear() {
    // Abort all active uploads
    this.activeUploads.forEach(({ abortController }) => {
      abortController?.abort();
    });
    
    this.queue = [];
    this.active = [];
    this.paused = [];
    this.activeUploads.clear();
  }

  // Get paused uploads
  getPaused(): UploadTask[] {
    return [...this.paused];
  }

  // Check if task is paused
  isPaused(taskId: string): boolean {
    return this.paused.some(t => t.id === taskId);
  }
}

// Singleton instance
export const uploadQueue = new UploadQueue({
  maxConcurrent: 5, // Increased to 5 as per requirements
  maxRetries: 3,
  retryDelay: 2000,
});

